'use client';

import * as React from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/util/cn';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { youtubeEmbedUrl, youtubeThumbnailUrl, youtubeVideoId } from '@/util/youtube';
import {
  Dialog, DialogContent, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

// Partner promo video for OnTheSnow+ resorts, rendered as a small poster
// thumbnail in the resort header (desktop only — see ResortHeader).
//
// The legacy HeroNav dropped a live 216x122 YouTube iframe straight into
// the header on every partner page, so every visitor paid for a
// third-party frame whether or not they watched it. Here the header only
// ever paints a static poster JPEG from i.ytimg.com and the player is
// mounted lazily, inside the dialog, on click.
//
// Consent: that legacy embed went through useOneTrustIframe with
// forceOneTrust, which hands the frame to OneTrust under C0004 Targeting
// Cookies — so it stayed blank for anyone who hadn't accepted that
// category. Loading it on page load, that gate was the right call. This
// player is different: it exists only after a deliberate click on a
// labelled control, which is the case the hook's own youtube-nocookie
// rewrite is there to serve (it skips the gate whenever a caller doesn't
// force it). We embed that same privacy-enhanced domain directly, so the
// frame is declarative React rather than an innerHTML insert that gets
// torn down and rebuilt whenever OneTrust's status settles.

export const PartnerVideo = ({
  url,
  className,
}: {
  /** Raw YouTube URL from `resort.video` / `resort.videoSummer`. */
  url: string;
  className?: string;
}) => {
  const { t } = useOTSTranslation('common');
  const [open, setOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const videoId = youtubeVideoId(url);

  // Anything the parser can't resolve to an 11-char id (a Vimeo link, a
  // truncated URL) renders nothing rather than a broken poster.
  if (!videoId) {
    return null;
  }

  const label = t('common.watchVideo');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        aria-label={label}
        className={cn(
          'group relative block w-48 shrink-0 overflow-hidden rounded-md border border-border lg:w-44',
          'bg-black shadow-sm transition hover:border-primary',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className,
        )}
      >
        <img
          src={youtubeThumbnailUrl(videoId)}
          alt=""
          loading="lazy"
          width={320}
          height={180}
          className="aspect-video w-full object-cover transition group-hover:opacity-90"
        />
        {/* Play badge — the only affordance signalling the poster is a
            video, so it stays legible against any frame YouTube picked. */}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white shadow-md transition group-hover:bg-primary">
            <Play size={14} className="ml-0.5 fill-current" />
          </span>
        </span>
      </DialogTrigger>

      <DialogContent
        ref={contentRef}
        className="max-w-3xl gap-0 border-0 bg-black p-0 sm:rounded-lg"
        // The player is the whole dialog; the sr-only title says all
        // there is to say, so opt out of Radix's description slot
        // rather than leaving a dangling aria-describedby.
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          // Radix focuses the first focusable child, which here is the
          // cross-origin iframe. Focus inside it means Escape keydowns go
          // to YouTube's document instead of ours, so the dialog can no
          // longer be closed by keyboard. Hold focus on the dialog itself
          // and let the viewer click into the player if they want it.
          event.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>
        <div className="relative aspect-video w-full overflow-hidden sm:rounded-lg">
          {/* Mounted only while open, so closing the dialog unmounts the
              frame and stops playback. */}
          {open && (
            <iframe
              src={youtubeEmbedUrl(videoId)}
              title={label}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 h-full w-full border-0"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
